/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useState, useRef, useEffect } from "react";
import gatewayFetch from "utilities/gatewayFetch";
import { millisecondsToSeconds, msToFullMinutes } from "TimeHelper";
import { mapFromServerFormat } from "utilities/ApiUtilities";

export type UserMetrics = {
  recordingDurationMins: number;
  quotaResetTimeMins: number;
  quotaUsedMins: number;
  quotaLimitMins: number;
};

type UserMetricsHook = {
  userMetrics: UserMetrics;
  refresh: () => Promise<void>;
  isError: boolean;
  isLoading: boolean;
};

const initialState: UserMetrics = {
  recordingDurationMins: 0,
  quotaResetTimeMins: 0,
  quotaUsedMins: 0,
  quotaLimitMins: 0,
};

export const useUserMetrics = (): UserMetricsHook => {
  const [userMetrics, setUserMetrics] = useState<UserMetrics>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(true); //True until someone calls refreshMetrics
  const cancelledRef = useRef(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    return (): void => {
      cancelledRef.current = true;
    };
  }, []);

  const fetchUserMetrics = useCallback(async function (): Promise<void> {
    setIsError(false);
    setIsLoading(true);
    let response;

    try {
      response = await gatewayFetch(`/api/v1/user_metrics`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
    } catch (e) {
      setIsError(true);
      setIsLoading(false);
      return;
    }

    if (!response.ok) {
      setIsError(true);
      throw new Error("Failed to fetch user metrics");
    }

    const userMetricsJson = mapFromServerFormat(await response.json());

    if (!cancelledRef.current) {
      setUserMetrics({
        recordingDurationMins: msToFullMinutes(
          userMetricsJson.recordingDurationMs
        ),
        quotaResetTimeMins: msToFullMinutes(userMetricsJson.quotaResetTimeMs),
        quotaUsedMins: msToFullMinutes(userMetricsJson.quotaUsedMs),
        quotaLimitMins: msToFullMinutes(userMetricsJson.quotaLimitMs),
      } as UserMetrics);

      setIsLoading(false);
    }
  }, []);

  return {
    userMetrics,
    isLoading,
    isError,
    refresh: fetchUserMetrics,
  };
};
